import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  BookTemplate,
  Camera,
  Clock,
  FileText,
  Landmark,
  Star,
  Users,
} from "lucide-react";
import { GiTeacher } from "react-icons/gi";
import { PiExam } from "react-icons/pi";
import { BsWhatsapp } from "react-icons/bs";
import { Link } from "react-router-dom";

const ProgramPage: React.FC = () => {
  useEffect(() => {
    AOS.init({ duration: 500, once: true });
  }, []);

  const programData = [
    {
      title: "Listening & Reading Comprehension",
      desc: "Build your ability to understand spoken and written Indonesian through practical materials and conversations relevant to everyday situations",
    },
    {
      title: "Everyday & Situational Conversation",
      desc: "Develop the confidence to communicate naturally in common situations, from everyday interactions to travel, work, and social settings",
    },
    {
      title: "Vocabulary & Pronunciation Practice",
      desc: "Expand your vocabulary while refining your pronunciation, so you can express yourself more clearly and naturally",
    },
    {
      title: "Grammar by BIPA Levels",
      desc: "Strengthen your grammar through structured lessons aligned with your BIPA level, with a focus on how grammar works in actual communication",
    },
    {
      title: "Context-based Communication",
      desc: "Learn how to choose the right words and expressions depending on the situation, relationship, and context in which you are communicating",
    },
    {
      title: "Indonesian Culture Integration",
      desc: "Gain a better understanding of Indonesian culture and the social context behind the language, helping you communicate with greater awareness and ease",
    },
  ];

  const listBenefits = [
    {
      icon: <Clock className="text-gray-700 w-4 h-4" />,
      title: "4-Week Program",
    },
    {
      icon: <GiTeacher className="text-gray-700 w-4 h-4" />,
      title: "Interactive Live Classes",
    },
    {
      icon: <PiExam className="text-gray-700 w-4 h-4" />,
      title: "Placement Test",
    },
    {
      icon: <BookTemplate className="text-gray-700 w-4 h-4" />,
      title: "BIPA Learning Module",
    },
    {
      icon: <FileText className="text-gray-700 w-4 h-4" />,
      title: "FREE E-Book & PDF Materials",
    },
    {
      icon: <BsWhatsapp className="text-gray-700 w-4 h-4" />,
      title: "Whatsapp Support Group",
    },
    {
      icon: <Landmark className="text-gray-700 w-4 h-4" />,
      title: "Indonesian Cultural Integration",
    },
    {
      icon: <Camera className="text-gray-700 w-4 h-4" />,
      title: "Free Class Recording",
    },
    {
      icon: <Clock className="text-gray-700 w-4 h-4" />,
      title: "E-Certificate of Completion",
    },
  ];

  const dataStats = [
    {
      icon: <Star className="text-gray-700 w-4 h-4" />,
      title: "Rating",
      desc: "4.6 / 5.0",
    },
    {
      icon: <Users className="text-gray-700 w-4 h-4" />,
      title: "Students",
      desc: "2-7 Students",
    },
  ];

  const whiteIndexes = [1, 2, 5];

  const whatsappMessage =
    "Halo Fluenesia, saya ingin memesan kelas Semi-Private. Mohon informasi lebih lanjut mengenai jadwal, harga, dan langkah pendaftarannya. Terima kasih!";

  const whatsappUrl = `https://api.whatsapp.com/send/?phone=6282324433012&text=${encodeURIComponent(
    whatsappMessage,
  )}&type=phone_number&app_absent=0`;

  return (
    <div className="relative w-full min-h-screen bg-[#FAFAFA] font-sans overflow-x-hidden">
      <div className="absolute top-0 left-0 w-full z-0">
        <img
          src="/Mentor/screen 3.png"
          alt="Decoration"
          className="top-0 h-full object-contain "
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-8 md:pt-14 pb-24">
        {/* GRID SYSTEM */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* --- KOLOM KIRI (HEADER + BODY TEXT) --- */}
          <div className="lg:col-span-8 space-y-12">
            {/* A. Bagian Header (Hero) */}
            <div data-aos="fade-right">
              {/* --- UPDATE: Breadcrumb dengan Link --- */}
              <div className="flex items-center space-x-2 font-medium text-sm md:text-base mb-3">
                <Link
                  to="/"
                  className="hover:underline cursor-pointer transition-colors"
                >
                  Home
                </Link>
                <span>▸</span>
                <Link
                  to="/program-selection"
                  className="hover:underline cursor-pointer transition-colors"
                >
                  Program
                </Link>
                <span>▸</span>
                <span className="text-[#FF0000]">Semi-Private</span>
              </div>
              {/* ----------------------------------- */}

              <h1 className="text-4xl md:text-6xl font-bold text-black leading-tight tracking-tight mb-3">
                Semi-Private Class
              </h1>

              <p className="text-lg md:text-xl text-gray-800 font-medium mb-6">
                Build the Indonesian skills you need for everyday life, travel,
                work, and connecting with Indonesian culture in an interactive
                small-group setting
              </p>

              {/* <button className="bg-[#FF0000] hover:bg-[#D6001C] text-white font-bold py-3 px-8 rounded-lg shadow-md transition-transform duration-300 transform hover:scale-105">
                Start Learning!
              </button> */}
            </div>

            {/* B. Bagian Body (Content Loop) */}
            {/* Padding top (pt) ditambahkan agar ada jarak pasca background pink habis */}
            <div className="space-y-6 pt-16 sm:pt-24 md:pt-24">
              <div data-aos="fade-up">
                <h3 className="text-2xl md:text-3xl font-bold text-black mb-4">
                  What You'll Work On
                </h3>
              </div>

              {/* Program Features */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 -pt-10">
                {programData.map((item, index) => (
                  <div
                    key={index}
                    className={`${whiteIndexes.includes(index) ? "bg-white border-[#FFB0B3] border-2" : "bg-[#FFE6E6]"} rounded-[30px] p-8 md:p-10 hover:shadow-lg transition-shadow duration-300 flex flex-col items-start text-left`}
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                  >
                    <div className="shrink-0 mt-0.5 mb-6">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="20" height="20" rx="4" fill="#00C800" />
                        <path
                          d="M5 10L8.5 13.5L15 6"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>

                    {/* Content */}
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            {/* Mentor Spotlight */}
            <div className="flex flex-col gap-4">
              <div className="bg-[#FFEBEB] border border-[#CC0007] rounded-[30px] p-8 md:p-10 hover:shadow-lg transition-shadow duration-300 flex text-left w-full items-center justify-between">
                <div className="w-24 h-24 border bg-[#ed8a8e] rounded-full flex-shrink-0">
                  <img
                    src="/Mentor/Ida.jpeg"
                    alt="Mentor Ida"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>

                <div className="flex flex-col justify-center pl-10 w-full">
                  <div className="bg-[#FFB0B3] px-5 w-max py-1 rounded-full mb-2">
                    <span className="text-[#CC0007] text-sm font-medium">
                      Mentor Spotlight
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                    Ida Fitriyah
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base text-wrap w-3/4">
                    Universitas Sebelas Maret - Pendidikan Bahasa Indonesia
                  </p>
                </div>
              </div>

              <div className="bg-[#FFEBEB] border border-[#CC0007] rounded-[30px] p-8 md:p-10 hover:shadow-lg transition-shadow duration-300 flex text-left w-full items-center justify-between">
                <div className="w-24 h-24 border bg-[#ed8a8e] rounded-full flex-shrink-0 overflow-hidden">
                  <img
                    src="/Mentor/Dira.jpeg"
                    alt="Mentor Dira"
                    className="w-full h-full object-cover object-[10%_1%] rounded-full"
                  />
                </div>

                <div className="flex flex-col justify-center pl-10 w-full">
                  <div className="bg-[#FFB0B3] px-5 w-max py-1 rounded-full mb-2">
                    <span className="text-[#CC0007] text-sm font-medium">
                      Mentor Spotlight
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                    Dian Rachmatika
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base text-wrap w-3/4">
                    University of Southampton, UK - English Language Teaching
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* --- KOLOM KANAN (CARD FLOATING) --- */}
          <div className="lg:col-span-4 lg:pt-10 relative" data-aos="fade-left">
            <div className="bg-white rounded-3xl shadow-2xl p-6 w-full border border-gray-100 sticky top-8">
              {/* Gambar Card */}
              <div className="w-full h-max bg-[#FF0000] rounded-2xl mb-6 shadow-inner flex items-center justify-center">
                <img
                  src="/Programs/semi-class.jpeg"
                  alt="Semi-Private Class"
                  className="w-full h-full object-cover rounded-2xl"
                />
              </div>

              <div className="space-y-4 mb-8">
                {/* Harga */}
                <div className="flex items-center space-x-1">
                  <span className="text-[#FF0000] text-2xl font-bold -mt-2">
                    Rp 2.000.000
                  </span>
                  <span className="text-gray-500 text-sm">/4 Session</span>
                </div>
                {/* Rating */}
                <div className="flex items-center space-x-3 w-full">
                  {dataStats.map((stat, index) => (
                    <span
                      key={index}
                      className="p-4 bg-[#FFE6E6] text-black text-sm font-medium rounded-2xl w-1/2"
                    >
                      <div className="flex items-center space-x-2">
                        {stat.icon}
                        <div className="flex flex-col">
                          <p>{stat.title}</p>
                          <p>{stat.desc}</p>
                        </div>
                      </div>
                    </span>
                  ))}
                </div>

                <div
                  style={{
                    borderTop: "none",
                    backgroundImage:
                      "linear-gradient(to right, #d1d5db 50%, transparent 50%)",
                    backgroundSize: "25px 1px",
                    backgroundRepeat: "repeat-x",
                    height: "1px",
                  }}
                />
                {/* Learners */}
                <div className="flex items-start space-x-3">
                  <span className="text-black text-lg font-medium">
                    Program Benefit's
                  </span>
                </div>
                {/* Duration */}
                {listBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    {benefit.icon}
                    <span className="text-black text-sm font-medium">
                      {benefit.title}
                    </span>
                  </div>
                ))}
              </div>

              <a href={whatsappUrl}>
                <button className="w-full bg-[#FF0000] hover:bg-[#D6001C] text-white font-bold py-3 px-4 rounded-xl shadow-md transition-all duration-300 transform hover:scale-[1.02]">
                  Start Learning!
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- KOMPONEN IKON ---
const IconWallet = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-gray-700"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);
const IconStar = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-gray-700"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
    />
  </svg>
);
const IconUsers = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-gray-700"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);
const IconClock = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-gray-700"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export default ProgramPage;
